import { useEffect, useRef } from 'react';
import { useTracker } from '../../state/store';

/**
 * Per-channel oscilloscopes.
 *
 * Every channel shares one canvas: 32 separate canvases would mean 32 contexts
 * and 32 repaints per audio snapshot, which is measurably worse than drawing
 * them all in a single pass.
 */
export function Scopes() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const song = useTracker((s) => s.song);
  const playback = useTracker((s) => s.playback);
  const mutedChannels = useTracker((s) => s.mutedChannels);
  const toggleMute = useTracker((s) => s.toggleMute);
  const toggleSolo = useTracker((s) => s.toggleSolo);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = container.clientWidth;
    const height = 56;
    if (width <= 0) return;

    canvas.width = Math.max(1, width * dpr);
    canvas.height = Math.max(1, height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = '#0b0b0e';
    ctx.fillRect(0, 0, width, height);

    const channels = song.channels;
    const slot = width / channels;
    const { scopes, scopeLength, peaks } = playback;

    for (let ch = 0; ch < channels; ch++) {
      const x0 = ch * slot;
      const muted = mutedChannels[ch];

      ctx.fillStyle = ch % 2 === 0 ? '#0e0e11' : '#101014';
      ctx.fillRect(x0, 0, slot - 1, height);

      // Level bar along the bottom edge.
      const peak = peaks?.[ch] ?? 0;
      if (peak > 0 && !muted) {
        ctx.fillStyle = peak > 0.9 ? '#ff5252' : peak > 0.6 ? '#ffb545' : '#06c167';
        ctx.fillRect(x0 + 1, height - 3, (slot - 3) * Math.min(1, peak), 2);
      }

      ctx.fillStyle = muted ? '#2a2a33' : '#565667';
      ctx.font = '8px "Spline Sans Mono", monospace';
      ctx.fillText(String(ch + 1).padStart(2, '0'), x0 + 3, 9);

      if (muted || !scopes || scopeLength === 0) continue;

      const mid = height / 2;
      const usable = Math.min(scopeLength, Math.max(8, Math.floor(slot * 3)));
      const step = usable / Math.max(1, slot - 4);

      ctx.strokeStyle = '#4ceb9b';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let px = 0; px < slot - 4; px++) {
        const index = ch * scopeLength + Math.floor(px * step);
        const value = scopes[index] ?? 0;
        const y = mid - value * (height * 0.36);
        if (px === 0) ctx.moveTo(x0 + 2 + px, y);
        else ctx.lineTo(x0 + 2 + px, y);
      }
      ctx.stroke();
    }
  }, [song.channels, playback, mutedChannels]);

  /** Click a scope to mute its channel; shift-click to solo it. */
  const onClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const channel = Math.floor(((event.clientX - rect.left) / rect.width) * song.channels);
    if (channel < 0 || channel >= song.channels) return;
    if (event.shiftKey) toggleSolo(channel);
    else toggleMute(channel);
  };

  return (
    <div ref={containerRef} className="w-full border-t border-ink-700 bg-ink-950">
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: 56 }}
        className="block cursor-pointer"
        onClick={onClick}
        title="Click to mute a channel, shift-click to solo"
      />
    </div>
  );
}
